import { memo, useCallback, useEffect, useState } from 'react';
import { logger } from '@zep/core-logger';
import { type QuizGenerateItem } from '@/api/ai/type';
import { type GenerationModeUiCohort, useGenerationModeUiCohort } from '@/api/ai/useGenerationModeUiCohort';
import { quotaExceededModal } from '@/shared/features/ai-quiz-generation/components';
import { useAiTrialQuotaQuery } from '@/shared/features/ai-quiz-generation/hooks';
import { clearAiAutoTriggerData, loadAiAutoTriggerData } from '@/shared/features/ai-quiz-generation/utils/aiAutoTriggerStorage';
import { MAX_QUIZ_COUNT } from '../constant';
import { useBuildInfoContext } from '../context/BuildInfoProvider';
import { adaptAiGeneratedQuizzesToBuildForm } from '../features/ai-quiz-generation/utils';
import { useShowQuizSetupModal } from '../features/quiz-setup-modal';
import { useBuildFormContext } from '../form/schema';
import { useBuildQuestionAlert } from '../hook/useBuildQuestionAlert';
import { useConfirmPersistData, useSetPersistButtonVisible } from '../hook/useConfirmPersistData';
import { buildAiModal } from '../modal/BuildAiModal';
import { type BuildAiCategoryContextValue } from '../modal/BuildAiModal/context/BuildAiCategoryProvider';
import { useOpenContinueBuildConfirmModal } from '../modal/ContinueBuildConfirmModal';
type AutoTriggerEffectProps = {
  isLoading: boolean;
  isQuotaExhausted: boolean;
  onSave: (quizzes: QuizGenerateItem[], generationId: string) => Promise<boolean>;
  onCheckQuizCountLimit: () => number;
  categoryProviderValue: BuildAiCategoryContextValue;
  uiCohort: GenerationModeUiCohort;
};

/**
 * Landing에서 전달된 AI prompt로 자동으로 AI 모달을 여는 컴포넌트.
 *
 * memo로 부모 re-render를 차단합니다.
 * isLoading/isQuotaExhausted props가 변경될 때만 re-render되고,
 * clearAiAutoTriggerData() 이후 loadAiAutoTriggerData()가 null을 반환하므로
 * Strict Mode 이중 마운트에서도 자연스럽게 1회 실행이 보장됩니다.
 */
const AutoTriggerEffect = memo(function AutoTriggerEffect({
  isLoading,
  isQuotaExhausted,
  onSave,
  onCheckQuizCountLimit,
  categoryProviderValue,
  uiCohort
}: AutoTriggerEffectProps) {
  useEffect(() => {
    if (isLoading) return;
    const autoTriggerData = loadAiAutoTriggerData();
    if (!autoTriggerData) return;
    if (isQuotaExhausted) {
      clearAiAutoTriggerData();
      quotaExceededModal.open();
    } else {
      const quizCountLimit = onCheckQuizCountLimit();
      buildAiModal.open({
        onSave,
        quizCountLimit,
        type: 'none',
        features: {
          initialPrompt: autoTriggerData.prompt,
          topicId: autoTriggerData.topicId,
          achievements: {
            enabled: true
          },
          autoTrigger: true
        },
        triggerSource: 'landing',
        uiCohort,
        categoryProviderValue
      });
      clearAiAutoTriggerData();
    }
  }, [isLoading, isQuotaExhausted, onSave, onCheckQuizCountLimit, categoryProviderValue, uiCohort]);
  return null;
});

/**
 * Build 페이지 초기 마운트 시 실행되는 컴포넌트.
 *
 * memo + useEffect([])로 마운트 1회만 실행됩니다.
 * onMount가 안정적인 참조이면 memo가 re-render를 완전히 차단합니다.
 */
const NormalMountEffect = memo(function NormalMountEffect({
  onMount
}: {
  onMount: () => Promise<void>;
}) {
  useEffect(() => {
    void onMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
});

/**
 * Build 페이지 초기화 통합 컴포넌트
 *
 * FormProvider 안에, SafeEnterTracking의 형제 노드로 배치합니다.
 * SafeEnterTracking의 isMounted 전환으로 인한 unmount/remount의 영향을 받지 않습니다.
 *
 * - Auto-trigger 데이터가 있으면: <AutoTriggerEffect /> → AI 모달 자동 오픈
 * - 없으면: <NormalMountEffect /> → 일반 mount effect 실행 (Quiz Setup Modal 등)
 */
export const BuildPageInitializer = () => {
  const {
    action,
    subjectTree,
    schoolInfoTree
  } = useBuildInfoContext();
  const {
    getValues,
    setValue
  } = useBuildFormContext();
  const [isAutoTriggerMode] = useState(() => loadAiAutoTriggerData() !== null);
  const uiCohort = useGenerationModeUiCohort();

  // Auto-trigger props
  const {
    isLoading,
    isQuotaExhausted
  } = useAiTrialQuotaQuery();
  const {
    openQuestionCountLimitAlert
  } = useBuildQuestionAlert();
  const {
    open: openQuizSetupModal
  } = useShowQuizSetupModal();
  const handleSave = useCallback(async (quizzes: QuizGenerateItem[], generationId: string): Promise<boolean> => {
    if (quizzes.length > MAX_QUIZ_COUNT) {
      openQuestionCountLimitAlert();
      return false;
    }
    const newQuizList = adaptAiGeneratedQuizzesToBuildForm(quizzes, generationId);
    setValue('quizList', [...getValues('quizList'), ...newQuizList]);
    buildAiModal.close();
    await openQuizSetupModal();
    return true;
  }, [getValues, setValue, openQuestionCountLimitAlert, openQuizSetupModal]);
  const handleCheckQuizCountLimit = useCallback(() => MAX_QUIZ_COUNT - getValues('quizList').length, [getValues]);

  // Normal mount props
  const {
    persistData,
    handleConfirmPersistData
  } = useConfirmPersistData();
  const setIsPersistButtonVisible = useSetPersistButtonVisible();
  const {
    open: openContinueBuildConfirmModal,
    isOpened: isOpenedContinueBuildConfirmModal
  } = useOpenContinueBuildConfirmModal();

  // useCallback(fn, [])으로 마운트 시점의 값을 캡처 (의도적 stale closure)
  // NormalMountEffect는 마운트 1회만 실행하므로 이 값들이 정확합니다.
  const handleMount = useCallback(async () => {
    if (action !== 'CREATE') return;
    if (persistData.data) {
      if (isOpenedContinueBuildConfirmModal.current) return;
      const confirmed = await openContinueBuildConfirmModal();
      if (confirmed) {
        handleConfirmPersistData(persistData);
        setIsPersistButtonVisible(false);
        logger.track('save_quiz_data', {
          btn: 'continue'
        });
        return;
      }
      const setupCompleted = await openQuizSetupModal();
      setIsPersistButtonVisible(!setupCompleted);
      return;
    }
    await openQuizSetupModal();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return isAutoTriggerMode ? <AutoTriggerEffect isLoading={isLoading} isQuotaExhausted={isQuotaExhausted} onSave={handleSave} onCheckQuizCountLimit={handleCheckQuizCountLimit} categoryProviderValue={{
    subjectTree,
    schoolInfoTree
  }} uiCohort={uiCohort} data-sentry-element="AutoTriggerEffect" data-sentry-component="BuildPageInitializer" data-sentry-source-file="BuildPageInitializer.tsx" /> : <NormalMountEffect onMount={handleMount} data-sentry-element="NormalMountEffect" data-sentry-component="BuildPageInitializer" data-sentry-source-file="BuildPageInitializer.tsx" />;
};